import {
  useState, useRef, UIEvent, useEffect,
} from 'react';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import IosShare from '@mui/icons-material/IosShare';
import Download from '@mui/icons-material/Download';
import { isGondolaApp } from 'lib/utils/mobileAppUtils';
import { CardUrls } from 'lib/types/replay';
import { ALL_SLIDES } from 'features/Replay2025/Replay2025';

const itemWidth = 215;

interface Props {
  imgUrls: CardUrls;
  onShare: (imageUrl: string, key: string) => void;
  onDownload: (imageUrl: string, key: string) => void;
  /** The key of the slide that is currently being viewed */
  currentCard?: string;
}

export const ShareableCarousel = ({
  imgUrls, onShare, onDownload, currentCard,
}: Props) => {
  const [scrollLeft, setScrollLeft] = useState(0);
  const carousel = useRef<HTMLDivElement>(null);

  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (currentCard) {
      const itemRef = itemRefs.current[currentCard];

      if (itemRef) {
        itemRef.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }
  }, [currentCard]);

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const scroll = e.currentTarget.scrollLeft;
    setScrollLeft(scroll);
  };

  const goLeft = () => {
    if (!carousel?.current) return;

    if (scrollLeft <= 0) return;
    carousel.current.scrollLeft = scrollLeft - itemWidth;
  };

  const goRight = () => {
    if (!carousel?.current) return;

    carousel.current.scrollLeft = scrollLeft + itemWidth;
  };

  const disableLeft = () => {
    if (!carousel?.current) return true;

    if (carousel.current.scrollLeft <= 0) return true;

    return false;
  };

  const disableRight = () => {
    if (!carousel?.current) return false;

    const sw = carousel.current.scrollWidth;
    const ow = carousel.current.offsetWidth;
    const noScroll = sw === ow;
    const endScroll = sw === ow + carousel.current.scrollLeft;
    if (noScroll || endScroll) return true;

    return false;
  };

  return (
    <div className="relative overflow-hidden">
      <button
        type="button"
        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white opacity-70 hover:opacity-100 disabled:opacity-30 rounded-full"
        onClick={goLeft}
        disabled={disableLeft()}
        aria-label="previous"
      >
        <KeyboardArrowLeft fontSize="large" />
      </button>
      <button
        type="button"
        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white opacity-70 hover:opacity-100 disabled:opacity-30 rounded-full"
        onClick={goRight}
        disabled={disableRight()}
        aria-label="next"
      >
        <KeyboardArrowRight fontSize="large" />
      </button>
      <div
        ref={carousel}
        onScroll={onScroll}
        className="relative flex gap-4 overflow-x-scroll scroll-smooth snap-x z-0 items-center"
      >
        {
          Object.entries(imgUrls || {})
            .sort(([keyA], [keyB]) => ALL_SLIDES.indexOf(keyA) - ALL_SLIDES.indexOf(keyB))
            .map(([key, img]) => (
              <div
                key={key}
                className="relative snap-start pt-3"
                ref={(el) => { itemRefs.current[key] = el; }}
              >
                <div className="absolute right-0">
                  <button onClick={() => onShare?.(img, key)} type="button" className="border-2 rounded-full p-1 border-orange bg-white bg-opacity-70">
                    <IosShare />
                  </button>
                  {!isGondolaApp && (
                    <button onClick={() => onDownload(img, key)} type="button" className="border-2 rounded-full p-1 border-orange bg-white bg-opacity-70">
                      <Download />
                    </button>
                  )}
                </div>
                <img className="w-full min-w-[300px] md:min-w-[250px]" src={img} alt="post" />
              </div>
            ))
        }
      </div>
    </div>
  );
};
